import { AddressViewModel, TollRoadSegmentModel } from "api";
import { geocoderSourceTypesEnum, regionTypesEnum, sourceTypesEnum } from "helpers/enums";
import { groupedCarRoutes, matchSegmentToSequence } from "helpers/mapGeometry";
import { buildQuery, containsQuery, ParamsFilters, queryParams } from "helpers/reqparams";
import omit from "lodash/omit";
import { alert } from "services/alert";
import { body, request } from "services/request";

const routeUrl = "api/Routes";
const mapUrl = "api/Map";

type MapSuggestion = {
    displayName: string;
    address: { village?: string; hamlet: string; city?: string; town: string };
    location: { coordinates: [] };
    geocoderSourceType: keyof typeof geocoderSourceTypesEnum;
    locationId: string;
};

const tollRoadColors: { [k: string]: string | string[] } = {
    platon: "#8ECAE6",
    m1bel: "#6929c4",
    m3ukr: "#b33dc6",
    m4don: "#d7658b",
    m11neva: "#5e569b",
    m12vostok: "#b28600",
    ccr: "#012749",
    others: ["#009d9a", "#8a3800", "#a56eff", "#FB8500"],
};

let tollRoadColorIndex = 0;
const getTollRoadColor = (tag: keyof typeof tollRoadColors) => {
    if (tollRoadColors[tag]) {
        return tollRoadColors[tag];
    } else {
        const color = tollRoadColors.others[tollRoadColorIndex++ % tollRoadColors.others.length];
        return (tollRoadColors[tag] = color);
    }
};

const geocoder = ({ lat, lng }: { lat: number; lng: number }) => {
    const reqParams = { shouldAlert: true, alertParams: { successAlerts: false, noContentAlerts: true } };
    return request(`${mapUrl}/GetAddress`, body({ latitude: lat, longitude: lng }), reqParams).then(
        ({ displayName, address, geocoderSourceType }) => {
            if (!address) {
                return null;
            }
            if (!address.village) {
                address.village = address.hamlet;
            }
            if (!address.city) {
                address.city = address.town;
            }
            return {
                location: { type: "Point", coordinates: [lng, lat] },
                address: displayName,
                geocoderSourceType,
                ...address,
            };
        },
    );
};

const getRoute = (
    points: [number, number],
    options: { silent?: boolean; routeOptions?: { routerProfile: string } } = {},
) => {
    const { silent, routeOptions } = options;
    const params = {
        MapObjectFrom: { Location: { coordinates: points[0], type: "Point" } },
        MapObjectTo: { Location: { coordinates: points[1], type: "Point" } },
        routeOptions,
    };
    return request(`${mapUrl}/GetRoute`, body(params)).then((data) => {
        if (!data || !data.routes || data.routes.length === 0) {
            !silent && alert({ message: "Маршрутов по данным точкам заявки не найдено.", type: "warning" });
            return null;
        }
        const route =
            data.routes.find((item: { profile: string }) => item.profile === routeOptions?.routerProfile) ||
            data.routes[0];
        data.concatedCoords = route.geometry.coordinates.map((item: [number, number]) => item.reverse());
        data.roadRestrictions = route.roadRestrictions;
        data.roadControlPoints = route.roadControlPoints || [];
        data.routeInfo = {
            distance: route.distance,
            duration: route.duration,
            tollSegments: route.tollSegmentsInfo?.segments,
        };
        if (data.routeInfo.tollSegments) {
            data.routeInfo.tollSegments.forEach((tollSegment: { color: string | string[]; tag: string }) => {
                tollSegment.color = getTollRoadColor(tollSegment.tag);
            });
        }
        return data;
    });
};

const formatSuggestions = (suggestion: MapSuggestion) => {
    const { displayName, address, location, geocoderSourceType, locationId } = suggestion;
    if (!address.village) {
        address.village = address.hamlet;
    }
    if (!address.city) {
        address.city = address.town;
    }
    return {
        location: location || { coordinates: [] },
        address: displayName,
        geocoderSourceType,
        locationId,
        ...address,
    };
};

const formatOrganizations = (suggestion: MapSuggestion) => {
    const { displayName, location, geocoderSourceType, locationId } = suggestion;
    return {
        location: location || { coordinates: [] },
        address: displayName,
        geocoderSourceType,
        locationId,
    };
};

const formatAddressViewModel = (suggestion?: AddressViewModel | null) => {
    if (!suggestion) {
        return null;
    }
    const { displayName, address, location, geocoderSourceType, locationId } = suggestion;
    return {
        location: location || { coordinates: [] },
        address: displayName || "",
        geocoderSourceType,
        locationId,
        ...(address || {}),
    };
};

const getMixedAddresses = (params: {}) => {
    return request(`${mapUrl}/GetMixedAddresses`, body(params)).then(({ suggestions }) => {
        if (!suggestions) {
            return [];
        }
        return suggestions.map(formatSuggestions);
    });
};

const getOrganizationAdresses = (params: {}) =>
    request(`${mapUrl}/getAddressesByOrganizationName`, body(params)).then(({ suggestions }) =>
        suggestions ? suggestions.map(formatOrganizations) : [],
    );

const getAddresses = (params: {}) =>
    request(`${mapUrl}/GetAddresses`, body(params)).then(({ suggestions }) => {
        if (!suggestions) {
            return [];
        }
        return suggestions.map(formatSuggestions);
    });

const getAddressesHereApi = (params: {}) =>
    request(`${mapUrl}/GetAddressesHereApi`, body(params)).then(({ suggestions }: { suggestions: MapSuggestion[] }) => {
        if (!suggestions) {
            return [];
        }
        return suggestions.map(({ displayName, address, location, geocoderSourceType, locationId }) => {
            if (!address.village) {
                address.village = address.hamlet;
            }
            if (!address.city) {
                address.city = address.town;
            }
            return {
                location: location || { coordinates: [] },
                address: displayName,
                geocoderSourceType,
                locationId,
                ...address,
            };
        });
    });

const getHereApiLocationById = (params: {}) => request(`${mapUrl}/GetHereApiLocationById`, body(params));

const createRoute = (params: {}) => request(`${routeUrl}/Apply`, body(params));

const checkPolygonArea = (params: {}) => request(`${mapUrl}/checkPolygonArea`, body(params));

const calculateRouteDistance = (params: { points: [number, number][] }) =>
    request(`${mapUrl}/calculateRouteDistance`, body(params));

const createDistributionBidRoute = (params: { bidId: number; distributionBidId: number }) => {
    const { bidId, ...rest } = params;
    rest.distributionBidId = bidId;
    return request(`${routeUrl}/ApplyDistribution`, body(rest));
};

const getRouteByBidId = (id: number) =>
    request(`${routeUrl}/Get/${id}`, { method: "GET" }).then((res) => {
        const route = matchSegmentToSequence(res);
        if (route) {
            if (!route.tollRoadSegments) {
                route.tollRoadSegments = [];
            }
            route.tollRoadSegments.forEach(
                (tollSegment: TollRoadSegmentModel & { color: string | string[]; tag: string }) => {
                    tollSegment.color = getTollRoadColor(tollSegment.tag);
                },
            );
        }
        return route;
    });

const getRouteByDistributionBidId = (id: number) => {
    return request(`${routeUrl}/GetDistribution/${id}`, { method: "GET" }).then((res) => {
        const route = matchSegmentToSequence(res);
        if (route) {
            if (!route.tollRoadSegments) {
                route.tollRoadSegments = [];
            }
            route.tollRoadSegments.forEach(
                (tollSegment: TollRoadSegmentModel & { color: string | string[]; tag: string }) => {
                    tollSegment.color = getTollRoadColor(tollSegment.tag);
                },
            );
        }
        return route;
    });
};

const getCarsRoutesByBidId = (id: number, showAll = false) =>
    request(`${routeUrl}/GetCarsRoutes/${id}${showAll ? "?showAll=true" : ""}`, { method: "GET" }).then((data) => {
        if (!data) {
            return data;
        }
        return groupedCarRoutes(data);
    });

const deleteRoute = (params: {}) => request(`${routeUrl}/Delete`, body(params));

const getLastCarsLocations = (params: { trailerTypeIds?: number[] }) => {
    const { trailerTypeIds, ...rest } = params;
    const trailersFilter = trailerTypeIds ? { typeId: { in: trailerTypeIds } } : {};
    const trailersQuery = encodeURIComponent(buildQuery({ filter: trailersFilter }).substr(1).replace(/'/g, ""))
        .replace("(", "%28")
        .replace(")", "%29");
    const trailersQueryString = trailersQuery ? `trailersQuery=${trailersQuery}&` : "";

    const query = Object.entries(rest).reduce(
        (acc, { 0: filterKey, 1: values }) => {
            acc[filterKey] = { in: values };
            return acc;
        },
        {} as { [key: string]: {} },
    );
    const filterQuery = buildQuery({ filter: query }).substr(1).replace(/'/g, "");

    return request(`${mapUrl}/GetLastCarsLocations?${trailersQueryString}${filterQuery}`, { method: "GET" });
};

const getLastCarLocationForBidId = (carId: number) =>
    request(`${mapUrl}/GetLastCarLocationForBid/${carId}`, { method: "GET" });

const getLastCarLocationAddress = (carId: number) =>
    request(`${mapUrl}/GetLastCarLocationAddress/${carId}`, { method: "GET" }).then(formatAddressViewModel);

const getRegions = (params: {}) => {
    return request(`${mapUrl}/GetRegions${containsQuery(params)}`, { method: "GET" });
};

const getRegionsByName = (params: { filters: ParamsFilters }, regionType?: keyof typeof regionTypesEnum) => {
    const idParam = params.filters.find((item) => item.dataIndex === "id");
    if (idParam) {
        if (Array.isArray(idParam.value)) {
            const ids = idParam.value.filter((id) => id !== null && id !== undefined && id !== "");
            if (!ids.length) {
                return Promise.resolve([]);
            }
            return Promise.all(ids.map((id) => request(`${mapUrl}/getRegion?id=${id}`, { method: "GET" }))).then(
                (regions) => regions.filter(Boolean),
            );
        }
        return request(`${mapUrl}/getRegion?id=${idParam.value}`, { method: "GET" }).then((res) => (res ? [res] : []));
    } else {
        const nameParam = params.filters.find((item) => item.dataIndex === "name");
        const queryParams = new URLSearchParams();
        if (nameParam) {
            queryParams.set("name", String(nameParam.value));
        }
        if (regionType) {
            queryParams.set("$filter", `type eq '${regionType}'`);
        }
        const query = queryParams.toString() ? `?${queryParams.toString()}` : "";
        return request(`${mapUrl}/getRegionsByName${query}`, { method: "GET" });
    }
};

const getFederalSubjects = (params: {}) => {
    const query = containsQuery(params, { filter: { type: regionTypesEnum.FederalSubject }, orderBy: "name asc" });
    return request(`${mapUrl}/getRegions${query}`, { method: "GET" });
};

const getTrackerLogs = (params: { sourceType: keyof typeof sourceTypesEnum }) => {
    const rest = omit(params, "sourceType");
    const query = buildQuery({ filter: rest }).substr(1);
    return request(`${mapUrl}/getTrackerLogs?sourceType=0&${query}`, { method: "GET" });
};

const findCarLocation = ({ params, itemChanges }: { params: {}; itemChanges: {} }) =>
    request(`${mapUrl}/findCarLocation${queryParams(params)}`, { method: "GET" }).then((data) => ({
        location: data && [data.x, data.y],
        itemChanges,
    }));

const getPaymentPointsList = () => request(`api/paymentPoints/getPoints?&top=6000`, { method: "GET" });

const getRoadRestrictionsList = () => request(`${routeUrl}/getRoadRestrictions`, { method: "GET" });

const getRoadRestrictionTypesForFilterList = () =>
    request(`${routeUrl}/getRoadRestrictionTypesForFilter`, { method: "GET" });

const getRegionDryingPeriodsList = (params: {}) => {
    const query = buildQuery({ filter: params }).substr(1);
    return request(`api/regionDryingPeriods/getPeriodsForMap?withDeleted=true&${query}`, { method: "GET" });
};

export {
    geocoder,
    getRoute,
    createRoute,
    calculateRouteDistance,
    createDistributionBidRoute,
    getRouteByBidId,
    getRouteByDistributionBidId,
    getCarsRoutesByBidId,
    deleteRoute,
    getLastCarsLocations,
    getLastCarLocationForBidId,
    getLastCarLocationAddress,
    getAddresses,
    getTrackerLogs,
    getMixedAddresses,
    getAddressesHereApi,
    getHereApiLocationById,
    getOrganizationAdresses,
    getRegions,
    getRegionsByName,
    findCarLocation,
    getPaymentPointsList,
    getRoadRestrictionsList,
    getRoadRestrictionTypesForFilterList,
    getRegionDryingPeriodsList,
    getFederalSubjects,
    checkPolygonArea,
};
